import React, { useState } from "react";
import { Grid, IconButton } from "@mui/material";
import { Search, AddCircleOutline as AddCircleOutlineIcon } from "@mui/icons-material";
import Input from "../../../Form/Input";
import Button, { Color } from "../../../Form/Button";

import useStyles from "./styles";
import { Skeleton } from "@mui/material";

interface ClassStyle {
  [propName: string]: string;
}

interface Props<T> {
  rightIcon?: React.ReactChild;
  classes?: ClassStyle;
  isLoading?: boolean;
  data: T[];
  renderChild?: React.ReactChild;
  addIntoList: (parameter: T) => void;
  extractInfo: (item: T) => { name: string; id?: number };
}

const LOADING_ITEMS = 20;

const SearchSection = ({
  classes,
  rightIcon,
  searchOnList,
}: {
  classes: any;
  rightIcon?: React.ReactChild;
  searchOnList: (search: string) => void;
}) => {
  const [search, setSearch] = useState<string>("");
  return (
    <Grid
      container
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      style={{ flexWrap: "nowrap" }}
    >
      <Input
        fullWidth
        name="search"
        placeholder="Search"
        value={search}
        formControlStyle={{ marginRight: rightIcon ? 0 : "15px" }}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          if (e.target.value === "") searchOnList("");
          setSearch(e.target.value);
        }}
        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key === "Enter") searchOnList(search);
        }}
        rightIcon={rightIcon}
      />
      {!rightIcon && (
        <Button customColor={Color.SECONDARY} onClick={() => searchOnList(search)}>
          <Search />
        </Button>
      )}
    </Grid>
  );
};

const renderFakeList = (classes?: ClassStyle) => {
  const arrayFake = [];

  for (let index = 0; index < LOADING_ITEMS; index++) {
    arrayFake.push(index);
  }

  return arrayFake.map((value) => {
    return (
      <li key={value} className={classes?.item}>
        <Skeleton variant="text" width="45%" height={40} />
        <Skeleton variant="circular" width="20px" height="20px" />
      </li>
    );
  });
};

const ListLoading = ({ classes }: { classes?: ClassStyle }) => {
  return <ul className={classes?.ulListOfItems}>{renderFakeList(classes)}</ul>;
};

const List = <T extends unknown>({ data, classes, addIntoList, extractInfo }: Props<T>) => {
  return (
    <ul data-cy="listOfItems" className={classes?.ulListOfItems}>
      {data.map((item, index: number) => (
        <li key={index} className={classes?.item}>
          <span className={classes?.textItem} onClick={() => addIntoList(item)}>
            {extractInfo(item).name}
          </span>
          <IconButton className={classes?.AddItemListInventory} onClick={() => addIntoList(item)}>
            <AddCircleOutlineIcon htmlColor="#308CDB" />
          </IconButton>
        </li>
      ))}
    </ul>
  );
};

const ListContent = <T extends unknown>({
  isLoading,
  rightIcon,
  data,
  renderChild,
  addIntoList,
  extractInfo,
}: Props<T>) => {
  const classes = useStyles();
  const [searchFilterData, setSearchFilterData] = useState<T[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);

  const handleSetSearch = (search: string) => {
    if (search === "") {
      setIsSearching(false);
      setSearchFilterData([]);
      return;
    }
    const result = data.filter((item) =>
      extractInfo(item).name.toLowerCase().includes(search.toLowerCase())
    );
    setIsSearching(true);
    setSearchFilterData(result);
  };

  const renderList = () => {
    if (isLoading) {
      return <ListLoading classes={classes} />;
    }

    return (
      <List
        data={isSearching ? searchFilterData : data}
        classes={classes}
        addIntoList={addIntoList}
        extractInfo={extractInfo}
      />
    );
  };

  return (
    <div className={classes.containerListContent}>
      {renderChild && <div className={classes.mb2}>{renderChild}</div>}
      <SearchSection
        classes={classes}
        rightIcon={rightIcon}
        searchOnList={(search: string) => handleSetSearch(search)}
      />
      {renderList()}
    </div>
  );
};

export default ListContent;
